/**
 * Personalisation enhancements for the product page.
 *
 * SAFETY CONTRACT — read before changing anything here.
 * This file is strictly ADDITIVE. It never renames, re-parents, clones or
 * re-creates a personalisation input, and it never touches its `name`,
 * `form`, `required` or `maxlength` attributes. Those are what bind
 * properties[...] to the /cart/add form, and a broken binding was the original
 * order-critical bug on this store. Everything below only READS inputs and
 * writes into sibling elements it creates itself.
 */

(() => {
  const PROP_SELECTOR = '[name^="properties["]';

  /** Engraved-look preview for a single-line personalisation field. */
  function buildWoodPreview(input) {
    if (input.dataset.esPreviewBound === 'true') return;

    const field = input.closest('.spacing-style, .__field, div') || input.parentElement;
    if (!field || !field.parentElement) return;

    const panel = document.createElement('div');
    panel.className = 'es-woodpreview';
    panel.setAttribute('aria-live', 'polite');

    const plate = document.createElement('div');
    plate.className = 'es-woodpreview__plate';

    const text = document.createElement('span');
    text.className = 'es-woodpreview__text';
    text.textContent = input.value || '';

    const placeholder = document.createElement('span');
    placeholder.className = 'es-woodpreview__placeholder';
    placeholder.textContent = 'Your engraving appears here';

    plate.append(text, placeholder);

    const note = document.createElement('p');
    note.className = 'es-woodpreview__note';
    // Deliberately modest wording: a placement guide, not a production proof.
    note.textContent =
      'A guide to how your text will sit — not an exact production proof. ' +
      'Final layout, font and spacing are set by the studio to suit the piece.';

    panel.append(plate, note);
    field.parentElement.insertBefore(panel, field.nextSibling);

    const sync = () => {
      const v = input.value || '';
      text.textContent = v;
      panel.dataset.empty = v.trim() === '' ? 'true' : 'false';
      plate.dataset.length = v.length > 28 ? 'long' : v.length > 16 ? 'medium' : 'short';
    };

    input.addEventListener('input', sync);
    sync();
    input.dataset.esPreviewBound = 'true';
  }

  /**
   * Upload fields: surface the chosen filename and reject the wrong type or an
   * oversized file in the browser, rather than letting it fail at checkout.
   */
  function buildUploadHelper(input) {
    if (input.dataset.esUploadBound === 'true') return;

    const MAX_BYTES = 20 * 1024 * 1024; // Shopify's cart upload ceiling
    const ALLOWED = ['image/jpeg', 'image/png', 'image/heic', 'image/webp'];

    const status = document.createElement('p');
    status.className = 'es-upload__status';
    status.setAttribute('role', 'status');
    input.parentElement.appendChild(status);

    // Thumbnail of the chosen file. Built from an object URL, so nothing is
    // uploaded, read or sent anywhere to produce it — the browser renders the
    // file the customer already selected. The URL is revoked on replacement
    // so a customer swapping photos does not leak blobs.
    const thumb = document.createElement('figure');
    thumb.className = 'es-upload__thumb';
    thumb.hidden = true;
    const thumbImg = document.createElement('img');
    thumbImg.alt = '';
    const thumbCap = document.createElement('figcaption');
    thumbCap.textContent = 'Your photo, as we received it. We engrave it in mono, sized to the piece.';
    thumb.append(thumbImg, thumbCap);
    input.parentElement.appendChild(thumb);

    let objectUrl = '';
    const clearThumb = () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      objectUrl = '';
      thumbImg.removeAttribute('src');
      thumb.hidden = true;
    };

    input.addEventListener('change', () => {
      const file = input.files && input.files[0];
      if (!file) {
        status.textContent = '';
        status.dataset.state = '';
        clearThumb();
        return;
      }

      const mb = (file.size / (1024 * 1024)).toFixed(1);

      if (!ALLOWED.includes(file.type)) {
        status.dataset.state = 'error';
        status.textContent = 'That file type is not supported. Please use JPG, PNG, HEIC or WEBP.';
        input.value = '';
        clearThumb();
        return;
      }

      if (file.size > MAX_BYTES) {
        status.dataset.state = 'error';
        status.textContent = 'That file is ' + mb + 'MB. Please upload an image under 20MB.';
        input.value = '';
        clearThumb();
        return;
      }

      status.dataset.state = 'ok';
      status.textContent = file.name + ' (' + mb + 'MB) ready to upload.';

      clearThumb();
      // HEIC has no browser decoder on most desktops; the file is still valid
      // for the order, so a failed preview must not read as a failed upload.
      objectUrl = URL.createObjectURL(file);
      thumbImg.onload = () => { thumb.hidden = false; };
      thumbImg.onerror = () => { clearThumb(); };
      thumbImg.src = objectUrl;
    });

    input.dataset.esUploadBound = 'true';
  }

  /** Live entry counter for the names-and-dates list. Display only. */
  function buildEntryCounter(field) {
    if (field.dataset.esCountBound === 'true') return;

    const wrap = field.closest('.es-entrylist');
    const out = wrap && wrap.querySelector('[data-es-entrycount]');
    if (!out) return;

    const max = parseInt(field.dataset.maxEntries || '0', 10);
    // Built from a string so no escaping survives a build step incorrectly.
    const NEWLINE = new RegExp('\\r?\\n');

    const sync = () => {
      const lines = (field.value || '').split(NEWLINE);
      let n = 0;
      for (const line of lines) {
        if (line.trim() !== '') n += 1;
      }
      out.textContent = String(n);
      wrap.dataset.over = max > 0 && n > max ? 'true' : 'false';
    };

    field.addEventListener('input', sync);
    sync();
    field.dataset.esCountBound = 'true';
  }

  /**
   * Engraving font choice: restyle the wood preview to match the selected
   * face. Display only — this never touches the radio's value, name or form
   * binding, so what reaches the order is exactly what the customer picked.
   */
  function applyFont(group) {
    const checked = group.querySelector('.es-fontpick__input:checked');
    if (!checked) return;
    const stack = checked.dataset.esFont || '';
    // Scope to the surrounding section so a second product form on the page
    // could never have its preview restyled by this one.
    const scope = group.closest('[id^="shopify-section"]') || document;
    scope
      .querySelectorAll('.es-woodpreview__text, .es-woodpreview__placeholder')
      .forEach((el) => {
        el.style.fontFamily = stack;
      });
    group.dataset.esFontActive = checked.value;
  }

  function bindFontPicker(group) {
    // The listener is attached once; the paint runs on every init. The wood
    // preview is built later in the same pass, and Horizon re-renders the
    // section on variant change — so re-applying is what keeps the preview
    // and the selected face in step.
    if (group.dataset.esFontBound !== 'true') {
      group.addEventListener('change', () => applyFont(group));
      group.dataset.esFontBound = 'true';
    }
    applyFont(group);
  }

  function init() {
    document.querySelectorAll('[data-es-entrylist]').forEach(buildEntryCounter);

    document.querySelectorAll(PROP_SELECTOR).forEach((input) => {
      if (input.type === 'file') {
        buildUploadHelper(input);
        return;
      }
      // The multi-entry list is a list, not one engraved line — a plate preview
      // would misrepresent it, so it is deliberately excluded.
      if (input.hasAttribute('data-es-entrylist')) return;

      // Not every line-item property is engraved text. A quantity ("12") or a
      // shape ("Heart") shown on a wood plate under the words "your engraving
      // appears here" would promise something the studio is not going to cut.
      // The block opts out explicitly rather than this guessing from the key.
      if (input.dataset.esPreview === 'false') return;

      if (input.tagName === 'TEXTAREA' || input.type === 'text') {
        buildWoodPreview(input);
      }
    });

    // After the previews exist, so the chosen face is painted on first load.
    document.querySelectorAll('.es-fontpick').forEach(bindFontPicker);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Horizon re-renders product sections on variant change; re-bind afterwards.
  document.addEventListener('shopify:section:load', init);
  new MutationObserver(() => init()).observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
})();
